import React, {useState, useEffect} from "react";
import {
    Box,
    Typography,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    IconButton,
    CircularProgress,
    Chip, 
    InputLabel, 
    Select, 
    MenuItem, 
    FormControl, 
    FormHelperText
} from '@mui/material';
import {
    DndContext,
    closestCenter,
    useDroppable,
    useDraggable,
    useSensors,
    useSensor,
    PointerSensor
} from '@dnd-kit/core';
import {SortableContext, horizontalListSortingStrategy} from '@dnd-kit/sortable';
import {useTheme} from "@mui/material/styles";
import {
    addBankBranch,
    removeBankBranch,
    getAvailableBranches,
    getTerminalIdsByBranch,
    updateTerminalIdsByBranch,
    getAllUsers,
    addNewTerminalToBranch, 
    getTimeZones
} from "../services/Request";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
    // deleteTerminalId, 
    updateBranchDetails
} from "../services/Update";
import {Snackbar} from "./Snackbar";
import Stack from "@mui/material/Stack";

const DraggableTerminal = ({id, terminalID, loadAvailableBranches, isEditing}) => {
    const {attributes, listeners, setNodeRef, transform, isDragging} = useDraggable({id});
    const theme = useTheme();
    // const [openDeleteTerminalDialog, setOpenDeleteTerminalDialog] = useState(false);

    return (
        <>
            <Chip
                ref={isEditing ? null : setNodeRef}
                {...(isEditing ? {} : {...listeners, ...attributes})}
                sx={{
                    transform: `translate3d(${transform?.x}px, ${transform?.y}px, 0)`,
                    opacity: isDragging ? 0.7 : 1,
                    backgroundColor: theme.palette.action.hover,
                    m: .5,
                    cursor: isEditing ? 'default' : 'pointer'
                }}
                label={terminalID}
                disabled={isEditing}
                // onDelete={() => setOpenDeleteTerminalDialog(true)}
            />
            {/*{openDeleteTerminalDialog && (*/}
            {/*    <DeleteTerminalIdDialog*/}
            {/*        openDeleteTerminalDialog={openDeleteTerminalDialog}*/}
            {/*        closeDeleteTerminalDialog={() => setOpenDeleteTerminalDialog(false)}*/}
            {/*        terminalGuid={id}*/}
            {/*        terminalId={terminalID}*/}
            {/*        loadAvailableBranches={loadAvailableBranches}*/}
            {/*    />*/}
            {/*)}*/}
        </>
    );
};
const DroppableBranch = ({id, children, setHoveredBranch, isEditing}) => {
    const {isOver, setNodeRef} = useDroppable({id});
    const theme = useTheme();

    const updateHoverStatus = (hovered) => {
        if (!isEditing) {
            setHoveredBranch(hovered ? id : null);
        }
    }

    return (
        <Grid
            container
            alignItems="center"
            ref={setNodeRef}
            sx={{
                width: '100%',
                minHeight: '50px',
                border: !isOver ? 1 : 2,
                borderColor: isOver ? theme.palette.primary.main : theme.palette.action.hover,
                borderRadius: 1,
                my: .5,
                display: 'flex',
                flexWrap: 'nowrap'
            }}
            onMouseEnter={() => updateHoverStatus(true)}
            onMouseLeave={() => updateHoverStatus(false)}
        >
            {children}
        </Grid>
    );
};

const BranchNameWithEdit = ({
                                branch,
                                hoveredBranch,
                                loadAvailableBranches,
                                isEditing,
                                availableTimeZones
                            }) => {
    const theme = useTheme();
    const [openBranchDialog, setOpenBranchDialog] = useState(false);

    const handleEditBranchName = () => {
        setOpenBranchDialog(true);
    };

    return (
        <Grid
            item
            xs={3}
            sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                minHeight: 50,
                minWidth: 120,
                backgroundColor: theme.palette.action.hover,
                pl: 1,
                position: 'relative',
                flexWrap: 'wrap'
            }}
        >
            <Stack>
                <Typography>{branch.branchName}</Typography>
                <Typography variant="caption" sx={{color: "text.secondary", fontStyle: "italic"}}>
                    {availableTimeZones.filter(x => x.timeZoneId == branch.timeZone)[0]?.timeZoneName}
                </Typography>
            </Stack>
            {hoveredBranch === branch.branchId && (
                <IconButton
                    title="Edit Branch Name"
                    aria-label="Edit Branch Name"
                    size="small"
                    sx={{ml: 1}}
                    onClick={handleEditBranchName}
                    disabled={isEditing}
                >
                    <EditIcon/>
                </IconButton>
            )}
            <BranchDialog
                openBranchDialog={openBranchDialog}
                setOpenBranchDialog={setOpenBranchDialog}
                branch={branch}
                loadAvailableBranches={loadAvailableBranches}
                availableTimeZones={availableTimeZones}
            />
        </Grid>
    );
};

const AddTerminalId = ({branchId, loadAvailableBranches, isEditing, setIsEditing}) => {
    const [newTerminalId, setNewTerminalId] = useState("");
    const [isAddingTerminal, setIsAddingTerminal] = useState(false);

    const handleAddTerminalId = async () => {
               if (!newTerminalId) return;
           
               try {
                   const { status, data } = await addNewTerminalToBranch(branchId, newTerminalId);
                   if (status === 200) {
                       await loadAvailableBranches();
                       setIsAddingTerminal(false);
                       setIsEditing(false);
                   } else {
                       Snackbar({ message: data?.message || "Error adding terminal ID.", variant: 'error' });
                   }
               } catch (error) {
                   Snackbar({ message: "Error adding terminal ID.", variant: 'error' });
               }
           };

    const handleCancelAdd = () => {
        setNewTerminalId("");
        setIsAddingTerminal(false);
        setIsEditing(false);
    };

    return (
        <>
            {isAddingTerminal ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: 200
                    }}
                >
                    <TextField
                        value={newTerminalId}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Only allow letters and numbers. Limit to 10 characters.
                            if (/^[a-zA-Z0-9]*$/.test(value) && value.length <= 10) {
                                setNewTerminalId(value);
                            }
                        }}
                        size="small"
                        variant="outlined"
                        sx={{
                            flexGrow: 1,
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleAddTerminalId();
                            }
                        }}
                    />
                    <IconButton
                        title="Save"
                        aria-label="Save"
                        size="small"
                        sx={{alignSelf: 'center'}}
                        onClick={handleAddTerminalId}
                        disabled={!newTerminalId}
                    >
                        <SaveIcon/>
                    </IconButton>
                    <IconButton
                        title="Cancel"
                        aria-label="Cancel"
                        size="small"
                        sx={{alignSelf: 'center'}}
                        onClick={handleCancelAdd}
                    >
                        <CancelIcon/>
                    </IconButton>
                </Box>
            ) : (
                !isEditing && (
                    <IconButton
                        title="Add Terminal ID"
                        aria-label="Add Terminal ID"
                        onClick={() => {
                            setIsAddingTerminal(true);
                            setIsEditing(true);
                        }}
                        size="small"
                        sx={{alignSelf: 'center'}}
                    >
                        <AddIcon fontSize="small"/>
                    </IconButton>
                )
            )}
        </>
    );
};

// const DeleteTerminalIdDialog = ({
//                                   openDeleteTerminalDialog,
//                                   closeDeleteTerminalDialog,
//                                   terminalGuid,
//                                   terminalId,
//                                   loadAvailableBranches
//                               }) => {
//
//     const handleDelete = async () => {
//         try {
//             const response = await deleteTerminalId(terminalGuid);
//             if (response.status === 200) {
//                 enqueueSnackbar('Terminal ID deleted successfully', {variant: 'success'});
//                 await loadAvailableBranches();
//                 closeDeleteTerminalDialog();
//             } else {
//                 enqueueSnackbar('Error deleting terminal ID', {variant: 'error'});
//             }
//         } catch (error) {
//             console.error('Error deleting terminal ID:', error);
//             enqueueSnackbar('Error deleting terminal ID', {variant: 'error'});
//         }
//     };
//
//     return (
//         <Dialog open={openDeleteTerminalDialog} onClose={closeDeleteTerminalDialog} fullWidth>
//             <DialogTitle>Delete Terminal ID</DialogTitle>
//             <DialogContent>
//                 <DialogContentText>
//                     Are you sure you want to delete the terminal ID <b>{terminalId}</b>?
//                 </DialogContentText>
//             </DialogContent>
//             <DialogActions>
//                 <Button onClick={closeDeleteTerminalDialog}>Cancel</Button>
//                 <Button variant="contained" onClick={handleDelete} autoFocus color="error">
//                     Delete
//                 </Button>
//             </DialogActions>
//         </Dialog>
//     );
// };

const BranchDialog = ({openBranchDialog, setOpenBranchDialog, branch, loadAvailableBranches, availableTimeZones}) => {
    const [branchName, setBranchName] = useState(branch?.branchName ?? "");
    const [timeZone, setTimeZone] = useState(branch?.timeZone ?? "");
    const [showHelperText, setShowHelperText] = useState(false);
    
    const handleBranchDetails = async () => {
        if (!branchName || !timeZone) {
            return;
        }

        try {
            const response = await updateBranchDetails(branch.branchId, branchName, timeZone);

            if (response.status === 200) {
                await loadAvailableBranches();
            } else {
                Snackbar({message: "Error updating branch details.", variant: 'error'});
            }
        } catch (error) {
            console.error("Error updating branch details: ", error);
        }
        
        handleCloseBranchDialog();
    };

    const handleConfirmNewBranch = async () => {
        if (!branchName || branch || !timeZone) {
            setOpenBranchDialog(false);
            return;
        }

        try {
            const response = await addBankBranch(branchName, timeZone);
            
            if (response.status === 200) {
                Snackbar({message: response?.data, variant: 'success'});
                loadAvailableBranches();
            } else {
                const errorMessage = response?.data?.message ?? "An unexpected error occurred.";
                Snackbar({message: errorMessage, variant: 'error'});
            }
        } catch (error) {
            Snackbar({message: "An unexpected error occurred.", variant: 'error'});
        }

        handleCloseBranchDialog();
    };

    function handleCloseBranchDialog() {
        setBranchName("")
        setTimeZone(null)
        setOpenBranchDialog(false);
    }

    return (
        <Dialog
            open={openBranchDialog}
            onClose={handleCloseBranchDialog}
            fullWidth
        >
            <DialogTitle>{branch ? "Edit Branch Details" : "Add New Branch"}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {branch ? "Edit the branch name and time zone." : "Enter the new branch name and time zone."}
                </DialogContentText>
                <FormControl fullWidth margin="normal" variant="outlined" error={showHelperText}>
                    <TextField
                        id="branchName"
                        label="Branch Name"
                        value={branchName}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Only allow letters and spaces. Limit to 30 characters.
                            if (/^[a-zA-Z\s]*$/.test(value) && value.length <= 30) {
                                setBranchName(value);
                                setShowHelperText(false);
                            }
                        }}
                        helperText={showHelperText ? <FormHelperText>Branch name required</FormHelperText> : ""}
                        error={showHelperText}
                        autoFocus
                        required
                    />
                </FormControl>

                <FormControl margin="normal" variant="outlined">
                <InputLabel htmlFor="timezone-selection">Time Zone</InputLabel>
                <Select
                    id="timezone-selection"
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    required
                    variant="outlined"
                    label="Time Zone"
                    sx={{minWidth: '200px'}}
                >
                    {availableTimeZones.map(option => {
                        return (
                            <MenuItem key={option.timeZoneId} value={option.timeZoneId}>
                                {option.timeZoneAbbreviation}
                            </MenuItem>
                        )
                    })}
                </Select>
            </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseBranchDialog}>Cancel</Button>
                <Button variant="contained" disabled={!branchName || !timeZone} onClick={branch ? handleBranchDetails : handleConfirmNewBranch} autoFocus>Submit</Button>
            </DialogActions>
        </Dialog>
    )
}

const CreateRemoveBranchDialog = ({
                                      openRemoveBranchDialog,
                                      setOpenRemoveBranchDialog,
                                      handleConfirmRemoveBranch,
                                      selectedBranch
                                  }) => {
    const removeBranch = async () => {
        handleCloseRemoveBranchDialog();
        handleConfirmRemoveBranch(selectedBranch?.branchId);
    }

    function handleCloseRemoveBranchDialog() {
        setOpenRemoveBranchDialog(false);
    }

    return (
        <Dialog
            open={openRemoveBranchDialog}
            onClose={handleCloseRemoveBranchDialog}
            fullWidth
        >
            <DialogTitle>Remove Branch</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to remove <b>{selectedBranch?.branchName}</b> branch?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseRemoveBranchDialog}>Cancel</Button>
                <Button onClick={removeBranch} autoFocus variant="contained" color="error">Remove</Button>
            </DialogActions>
        </Dialog>
    )
}

export const BranchSources = () => {
    const [availableBranches, setAvailableBranches] = useState([]);
    const [openBranchDialog, setOpenBranchDialog] = useState(false);
    const [openRemoveBranchDialog, setOpenRemoveBranchDialog] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [hoveredBranch, setHoveredBranch] = useState(null);
    const [availableTimeZones, setAvailableTimeZones ] = useState([]);
    const theme = useTheme();
    useEffect(() => {
        loadOptions();
    }, []);
    const loadOptions = async () => {
        const options = await getTimeZones();
        setAvailableTimeZones(options.data);
    };
    const loadAvailableBranches = async () => {
        setIsLoading(true);
        try {
            const branches = await getAvailableBranches();
            
            const branchTerminals = await Promise.all(
                    branches.map(async (branch) => {
                        const response = await getTerminalIdsByBranch(branch.id);
                        return {branchId: branch.id, branchName: branch.name, timeZone: branch.timeZoneId, terminals: response.data};
                    })
                );
                setAvailableBranches(branchTerminals);
            } catch (error) {
                console.error("Error fetching available branches: ", error);
            }finally {
            setIsLoading(false);
        }
    };
    
    useEffect(() => {
        loadAvailableBranches();

    }, []);

    async function updateTerminalOnBranch(branchId, terminalId) {
        try {
            return await updateTerminalIdsByBranch(branchId, terminalId);
        } catch (error) {
            console.error("Error updating terminals on branches: ", error);
        }
    }

    const handleDragEnd = async (event) => {
        const {over, active} = event;
        if (!over || over.id === active.id) return;

        const terminalBranch = availableBranches.find(branch => branch.terminals.some(terminal => terminal.id === active.id));
        const targetBranch = availableBranches.find(branch => branch.branchId === over.id);

        if (terminalBranch && targetBranch && terminalBranch.branchId !== targetBranch.branchId) {
            const movedTerminal = terminalBranch.terminals.find(terminal => terminal.id === active.id);

            // Temporarily update the terminal on the target branch for UI purposes
            setAvailableBranches(prevState => prevState.map(branch => {
                if (branch.branchId === terminalBranch.branchId) {
                    return {...branch, terminals: branch.terminals.filter(terminal => terminal.id !== active.id)};
                }
                if (branch.branchId === targetBranch.branchId) {
                    return {...branch, terminals: [...branch.terminals, movedTerminal]};
                }
                return branch;
            }));

            const response = await updateTerminalOnBranch(targetBranch.branchId, active.id);

            // If the update fails, revert the changes
            if (response.status !== 200) {
                setAvailableBranches(prevState => prevState.map(branch => {
                    if (branch.branchId === terminalBranch.branchId) {
                        return {...branch, terminals: [...branch.terminals, movedTerminal]};
                    }
                    if (branch.branchId === targetBranch.branchId) {
                        return {...branch, terminals: branch.terminals.filter(terminal => terminal.id !== active.id)};
                    }
                    return branch;
                }));
            }
        }
    };

    const handleAddBranch = () => {
        setOpenBranchDialog(true);
    };

    const handleRemoveBranch = async (branch) => {
        const existingBranch = availableBranches.find(b => b.branchId === branch.branchId);

        // Check to ensure all terminal IDs have been removed before deleting
        if (existingBranch?.terminals.length > 0) {
            Snackbar({message: "Remove all attached terminal IDs before deleting the branch.", variant: 'error'});
            return;
        }

        // Check to ensure there are no users that have the current branch available to them
        const response = await getAllUsers();
        if (response && response.some(user => user.branches.some(branchObj => branchObj.branchId === branch.branchId))) {
            Snackbar({message: "Remove all users from the branch before deleting it", variant: 'error'});
            return;
        }

        setSelectedBranch(existingBranch);
        setOpenRemoveBranchDialog(true);
    }

    const handleConfirmRemoveBranch = async (branchId) => {
        try {
            const response = await removeBankBranch(branchId);
            const message = response.data;
            Snackbar({message: message, variant: response.status === 200 ? 'success' : 'error'});
            if (response.status === 200) {
                setAvailableBranches(availableBranches.filter(b => b.branchId !== branchId));
                setSelectedBranch(null);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Minimum px distance to enable drag
            },
        })
    );

    return (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
            <Grid item xs={12}>
                <Button
                    variant="text"
                    onClick={handleAddBranch}
                    sx={{mt: 1}}
                    disabled={isEditing}
                >
                    <AddIcon/> Add New Branch
                </Button>
            </Grid>
            <Grid container sx={{
                width: '99%',
                height: 400,
                alignContent: 'flex-start',
                overflowX: 'auto',
                overflowY: 'auto',
                border: 1,
                borderRadius: 1,
                borderColor: theme.palette.action.hover,
                px: 1,
                py: .5,
                ml: 1,
                mt: 1,
            }}>
                {isLoading ? (
                    <Grid container justifyContent="center" alignItems="center" sx={{height: '100%'}}>
                        <CircularProgress size={60}/>
                    </Grid>
                ) : (
                    availableBranches.map((branch) => (
                        <DroppableBranch
                            key={branch.branchId}
                            id={branch.branchId}
                            setHoveredBranch={setHoveredBranch}
                            isEditing={isEditing}
                        >
                            <BranchNameWithEdit
                                branch={branch}
                                loadAvailableBranches={loadAvailableBranches}
                                hoveredBranch={hoveredBranch}
                                isEditing={isEditing}
                                availableTimeZones={availableTimeZones}
                                
                            />
                            <Grid item xs={8} sx={{
                                p: 1,
                                display: 'flex',
                                flexWrap: 'wrap',
                                minWidth: 300
                            }}>
                                <SortableContext items={branch.terminals} strategy={horizontalListSortingStrategy}>
                                    {branch.terminals.map((terminal) => (
                                        <DraggableTerminal key={terminal.id}
                                                         id={terminal.id}
                                                         terminalID={terminal.terminalID}
                                                         loadAvailableBranches={loadAvailableBranches}
                                                         isEditing={isEditing}
                                        />
                                    ))}

                                    {hoveredBranch === branch.branchId && (
                                        <AddTerminalId branchId={branch.branchId}
                                                     hoveredBranch={hoveredBranch}
                                                     loadAvailableBranches={loadAvailableBranches}
                                                     isEditing={isEditing}
                                                     setIsEditing={setIsEditing}
                                        />
                                    )}
                                </SortableContext>
                            </Grid>
                            <Grid item xs={1} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderLeft: 1,
                                borderColor: theme.palette.action.hover,
                                height: '100%',
                                marginLeft: 'auto'
                            }}>
                                <IconButton
                                    title="Delete Branch"
                                    aria-label="Delete Branch"
                                    onClick={() => handleRemoveBranch(branch)}
                                    disabled={isEditing}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                            </Grid>
                        </DroppableBranch>
                    ))
                )}
            </Grid>
            <BranchDialog openBranchDialog={openBranchDialog}
                          setOpenBranchDialog={setOpenBranchDialog}
                          branch={null}
                          loadAvailableBranches={loadAvailableBranches}
                          availableTimeZones={availableTimeZones}
            />
            <CreateRemoveBranchDialog openRemoveBranchDialog={openRemoveBranchDialog}
                                      setOpenRemoveBranchDialog={setOpenRemoveBranchDialog}
                                      handleConfirmRemoveBranch={handleConfirmRemoveBranch}
                                      selectedBranch={selectedBranch}
            />
        </DndContext>
    );
};